/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */

import { useEffect, useState } from "react";
import dynamic from "next/dynamic";

import {
  AssetType,
  DataProvider,
  getPlayable,
  IConfigurationModel,
  IMediaModel,
  MediaStreamType,
  MediaType,
  useGetMedia,
  useSessionTokenSelector,
} from "@nf/common";
import { REDIRECT_URL_PARAM, ROUTES } from "@nf/constants";
import {
  useMediaDetailsActionsSelector,
  useMediaDetailsMediaSelector,
} from "screens/MediaDetailsScreen";
import { WatchSEO } from "components";
import { RouteHelper } from "helpers";

import styles from "./Watch.module.scss";
import { googleAnalyticsEvent } from "../../lib/gtm";
import {
  useHideCookieConsentButton,
  useMediaParentalRatingGuard,
  useRouter,
  useSyncCompleted,
} from "../../hooks";

const VideoPlayer = dynamic(() => import("components/VideoPlayer"), {
  ssr: false,
});

export type IWatchScreenRouteProps = {
  id?: string;
  TRIAL?: string;
};

export interface WatchProps {
  configuration: IConfigurationModel;
}

export const Watch = (props: WatchProps) => {
  const router = useRouter();
  const { id } = router.query as IWatchScreenRouteProps;
  const playTrial = MediaStreamType.Trial in router.query;
  const media = useMediaDetailsMediaSelector();
  const { isLoading, error } = useMediaDetailsActionsSelector();
  const [displayMedia, setDisplayMedia] = useState(media);
  const isAuthProcessCompleted = useSyncCompleted();
  const sessionToken = useSessionTokenSelector();

  const mustSignIn = isAuthProcessCompleted && !sessionToken && !playTrial;

  useHideCookieConsentButton();

  useEffect(() => {
    if (mustSignIn && id) {
      router.replace({
        pathname: ROUTES.LOGIN,
        query: { [REDIRECT_URL_PARAM]: `${ROUTES.PLAYER_SCREEN}/${id}` },
      });
    }
  }, [mustSignIn, id]);

  useMediaParentalRatingGuard(media);

  const getMedia = useGetMedia();

  useEffect(() => {
    if (media) {
      setDisplayMedia(media);
    } else if (error) {
      // Stop showing the previous media if fetching the new one failed,
      // rather than getting stuck on stale, already-ended playback.
      setDisplayMedia(undefined);
    }
  }, [media, error]);

  useEffect(() => {
    media && media.MediaTypeCode && googleAnalyticsEvent.trackMovieEnter(media);
  }, [media]);

  useEffect(() => {
    if (id && media?.Id !== id && !isLoading) {
      getMedia({
        MediaId: id,
      });
    }
  }, [id, isLoading, media]);

  const compareByOrderDescending = (a: IMediaModel, b: IMediaModel) => {
    if (a.OrderInParent && b.OrderInParent) {
      return b.OrderInParent - a.OrderInParent;
    } else {
      return 0;
    }
  };

  const goToChildMedia = async (parentMedia: IMediaModel) => {
    if (media && media.MediaTypeCode === MediaType.Series) {
      const episode = await DataProvider.getLastViewEpisode(media?.Id);

      router.replace(RouteHelper.goToPlayer(episode));
    } else if (parentMedia.Media) {
      const sortedMedia = parentMedia.Media.filter(getPlayable).sort(
        compareByOrderDescending
      );
      const firstMedia = sortedMedia[0];

      if (firstMedia) {
        router.replace(RouteHelper.goToPlayer(firstMedia));
      }
    }
  };

  if (
    displayMedia &&
    displayMedia.MediaTypeCode !== AssetType.Video &&
    displayMedia.MediaTypeCode !== AssetType.Channel &&
    displayMedia.MediaTypeCode !== AssetType.Episode
  ) {
    // If it isn't VOD, probably it'sSERIES or SEASON - then play nested media.
    goToChildMedia(displayMedia);
    return null;
  }

  if ((isLoading && !displayMedia) || !isAuthProcessCompleted || mustSignIn) {
    return null;
  }

  if (!displayMedia) return null;

  return (
    <>
      <WatchSEO />
      <div className={styles.container}>
        <VideoPlayer
          config={props?.configuration}
          media={displayMedia}
          playTrial={playTrial}
        />
      </div>
    </>
  );
};

export default Watch;
